/**
 * A replay run: N frames, in order, against ONE pinned node.
 *
 * ## Dependencies are the whole isolation argument
 *
 * `ReplayRunDeps` has exactly two calls out: `runPipeline` (per frame, the
 * SAME `pipelineExecutor.runPipeline` production dispatch uses) and
 * `runTracking` (once, over every frame that succeeded — wraps
 * `pipelineAnalytics.runReplayFrameProcessor`, the FrameProcessor pass in
 * `addon-post-analysis`). There is no `trackStore`, no `mediaStore`, no
 * event bus to inject — this addon does not have one (addons don't import
 * each other), so the isolation `scripts/check-replay-isolation.ts` proves
 * for the `replay/` directories is reinforced HERE by the type itself: the
 * shape of `ReplayRunDeps` cannot express a write to production state. See
 * the "unica dipendenza" test in `replay-run.spec.ts`.
 *
 * ## Where it runs
 *
 * `nodeId` is REQUIRED and stamped on every `runPipeline` call — never left
 * to "wherever an unowned call lands". A run that changed node mid-flight
 * would compare two engines against itself.
 *
 * ## Why one bad frame doesn't stop the run
 *
 * A frame the node refuses (busy, transient) is counted and logged, and the
 * run continues — a 39/40 census is still a census; a run that aborts on
 * the first hiccup is not. `complete` says whether every frame that was
 * PLANNED actually RAN; a caller that needs "was ANYTHING seen at all" reads
 * `kind` instead, because zero frames run and zero detections found render
 * as the identical empty screen otherwise.
 */
import type { FrameResult, IScopedLogger } from '@camstack/types'
import type { ReplayDiscarded, ReplayTrack } from './replay-diff.js'

export interface ReplayFrame {
  readonly timestamp: number
  readonly image: Uint8Array
}

export interface ReplayRunDeps {
  readonly deviceId: number
  /** The node every `runPipeline` call is pinned to — never optional, never
   *  the hub by default (`nodeId` obbligatorio nel piano). */
  readonly nodeId: string
  readonly runPipeline: (input: {
    readonly timestamp: number
    readonly image: Uint8Array
    readonly nodeId: string
  }) => Promise<FrameResult>
  /** The FrameProcessor pass, called ONCE over every frame that succeeded,
   *  in order — wraps `pipelineAnalytics.runReplayFrameProcessor`. */
  readonly runTracking: (
    frames: readonly { readonly timestamp: number; readonly frame: FrameResult }[],
  ) => Promise<{ readonly tracks: readonly ReplayTrack[] }>
  readonly logger: IScopedLogger
  readonly now?: () => number
  /** Hard wall-clock ceiling for the whole run. Checked before EACH frame —
   *  a run that would exceed it stops there and reports `complete: false`,
   *  never a silent truncation. */
  readonly budgetMs?: number
  /** Delay between frames — same knob, same default (50ms) as the rebuild
   *  pass (`docs/architecture/post-analysis.md:717`), so a replay run
   *  shares its rate-limiting story with the other bulk pass this repo
   *  already runs against a live node. */
  readonly pacingMs?: number
}

export interface ReplayVerdict {
  /** `no-frame-ran` when NOT EVEN ONE frame executed — distinct from a
   *  legitimate zero-detections run, which is `ran` with empty `tracks`. */
  readonly kind: 'ran' | 'no-frame-ran'
  readonly deviceId: number
  readonly nodeId: string
  /** True only when every PLANNED frame ran — a budget stop or any frame
   *  failure both clear it. */
  readonly complete: boolean
  readonly tracks: readonly ReplayTrack[]
  readonly discarded: readonly ReplayDiscarded[]
  readonly counters: {
    readonly framesPlanned: number
    readonly framesRan: number
    readonly framesFailed: number
  }
}

export const DEFAULT_REPLAY_BUDGET_MS = 120_000
export const DEFAULT_REPLAY_PACING_MS = 50

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export async function runReplay(
  deps: ReplayRunDeps,
  frames: readonly ReplayFrame[],
): Promise<ReplayVerdict> {
  const now = deps.now ?? Date.now
  const budgetMs = deps.budgetMs ?? DEFAULT_REPLAY_BUDGET_MS
  const pacingMs = deps.pacingMs ?? DEFAULT_REPLAY_PACING_MS
  const startedAt = now()
  const tags = { deviceId: deps.deviceId }

  deps.logger.info('replay: run starting', {
    tags,
    meta: { framesPlanned: frames.length, nodeId: deps.nodeId, pacingMs, budgetMs },
  })

  const succeeded: { timestamp: number; frame: FrameResult }[] = []
  const discarded: ReplayDiscarded[] = []
  let framesFailed = 0
  let stoppedOnBudget = false

  for (let i = 0; i < frames.length; i++) {
    if (now() - startedAt >= budgetMs) {
      stoppedOnBudget = true
      deps.logger.warn('replay: run stopped on budget — a partial census, reported as such', {
        tags,
        meta: {
          framesPlanned: frames.length,
          framesRan: succeeded.length,
          framesFailed,
          budgetMs,
        },
      })
      break
    }
    const f = frames[i]
    if (f === undefined) continue
    try {
      const frame = await deps.runPipeline({
        timestamp: f.timestamp,
        image: f.image,
        nodeId: deps.nodeId,
      })
      succeeded.push({ timestamp: f.timestamp, frame })
      if (frame.discarded !== undefined) discarded.push(...frame.discarded)
    } catch (err) {
      framesFailed += 1
      deps.logger.warn('replay: frame failed on the node — counted, run continues', {
        tags,
        meta: { timestamp: f.timestamp, error: err instanceof Error ? err.message : String(err) },
      })
    }
    if (pacingMs > 0 && i < frames.length - 1) await sleep(pacingMs)
  }

  const tracks = succeeded.length > 0 ? (await deps.runTracking(succeeded)).tracks : []
  const complete = !stoppedOnBudget && framesFailed === 0

  deps.logger.info('replay: run finished', {
    tags,
    meta: {
      framesPlanned: frames.length,
      framesRan: succeeded.length,
      framesFailed,
      complete,
      tracks: tracks.length,
    },
  })

  return {
    kind: succeeded.length === 0 ? 'no-frame-ran' : 'ran',
    deviceId: deps.deviceId,
    nodeId: deps.nodeId,
    complete,
    tracks,
    discarded,
    counters: { framesPlanned: frames.length, framesRan: succeeded.length, framesFailed },
  }
}
